import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

type MaybeNumber = number | string | null

interface DmlCardDataItem {
    ATE?: MaybeNumber
    Effect_Size_Unit_Measurement?: MaybeNumber
    Lift?: MaybeNumber
    Total_Effect_From_Exp?: MaybeNumber
    P_Value_Primary?: MaybeNumber
    P_Value?: MaybeNumber
    P_Value_Secondary?: MaybeNumber
    total_kpi_impact?: MaybeNumber
    totalImpact?: MaybeNumber
    total_effect?: MaybeNumber
    average_treatment_effect?: MaybeNumber
    ate?: MaybeNumber
    impact?: MaybeNumber
    percentage_lift?: MaybeNumber
    uplift?: MaybeNumber
    lift_pct?: MaybeNumber
    p_value?: MaybeNumber
    Level_Status?: string
}

interface Props {
    dmlCardData?: DmlCardDataItem[] | DmlCardDataItem | null
    miniview?: boolean
}

const toNumber = (value?: MaybeNumber): number | null => {
    if (value === null || value === undefined || value === '') return null

    const numericValue = Number(value)
    return Number.isFinite(numericValue) ? numericValue : null
}

const pickNumber = (...values: (MaybeNumber | undefined)[]) => {
    for (const value of values) {
        const numericValue = toNumber(value)
        if (numericValue !== null) {
            return numericValue
        }
    }

    return null
}

const unwrapDmlCardData = (cardData: Props['dmlCardData']): DmlCardDataItem | null => {
    if (!cardData) return null

    if (Array.isArray(cardData)) {
        return (cardData[0] ?? null) as DmlCardDataItem | null
    }

    if (Array.isArray((cardData as any)?.data)) {
        return ((cardData as any).data[0] ?? null) as DmlCardDataItem | null
    }

    if (Array.isArray((cardData as any)?.result)) {
        return ((cardData as any).result[0] ?? null) as DmlCardDataItem | null
    }

    if ((cardData as any)?.dmlCardDetails) {
        return unwrapDmlCardData((cardData as any).dmlCardDetails)
    }

    return cardData as DmlCardDataItem
}

const formatSigned = (value: number | null, digits = 1) => {
    if (value === null) return '—'

    const normalized = Number(value)
    let prefix = ''
    if (normalized > 0) {
        prefix = '+'
    } else if (normalized < 0) {
        prefix = '-'
    }

    return `${prefix}${Math.abs(normalized).toLocaleString('en-US', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    })}`
}

const formatPercent = (value: number | null, digits = 1) => {
    const formatted = formatSigned(value, digits)

    return formatted === '—' ? formatted : `${formatted}%`
}

const formatPlain = (value: number | null, digits = 3) => {
    if (value === null) return '—'

    return Number(value).toLocaleString('en-US', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    })
}

const formatCompactSigned = (value: number | null) => {
    if (value === null) return '—'

    const normalized = Number(value)
    let prefix = ''
    if (normalized > 0) {
        prefix = '+'
    } else if (normalized < 0) {
        prefix = '−'
    }

    const absolute = Math.abs(normalized)

    if (absolute >= 1000000) {
        return `${prefix}${(absolute / 1000000).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}M`
    }

    if (absolute >= 1000) {
        return `${prefix}${(absolute / 1000).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}K`
    }

    return `${prefix}${absolute.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`
}

const DmlCards: React.FC<Props> = ({ dmlCardData = null, miniview = false }) => {
    const { t } = useTranslation()
    const payload = useMemo<DmlCardDataItem>(() => unwrapDmlCardData(dmlCardData) ?? {}, [dmlCardData])

    const liftPct = pickNumber(payload.Lift, payload.percentage_lift, payload.uplift, payload.lift_pct)
    const totalImpact = pickNumber(payload.Total_Effect_From_Exp, payload.total_kpi_impact, payload.totalImpact, payload.total_effect, payload.impact)
    const waldPValue = pickNumber(payload.P_Value_Primary, payload.p_value, payload.P_Value)

    return (
        <div className={`grid gap-3 md:grid-cols-3 ${miniview ? 'text-[.6rem]' : 'text-[1rem]'}`} data-aos="zoom-in" data-aos-delay="200">
            <article className="relative rounded-[0.5rem] bg-[#FFE600] px-4 py-3 pr-24 shadow-sm">
                <span className="block text-[0.75em] font-semibold text-[#111315]">{t('Estimated Uplift')}</span>
                <strong className="mt-2 block text-[1.625em] font-bold leading-none text-[#111315]">{formatPercent(liftPct, 1)}</strong>
                {!miniview && <p className="mt-2 text-[0.625em] text-[#4E535B]">{t('Estimated percentage improvement versus no intervention.')}</p>}
                <span title={payload?.Level_Status?.split('_').join(' ') || '-'} className="absolute right-4 top-3 max-w-[7rem] truncate text-nowrap capitalize text-black">
                    {payload?.Level_Status?.split('_').join(' ') || '-'}
                </span>
            </article>

            <article className="rounded-[0.5rem] bg-[#FFE600] px-4 py-3 shadow-sm">
                <span className="block text-[0.75em] font-semibold text-[#111315]">{t('Total KPI Impact')}</span>
                <strong className="mt-2 block text-[1.625em] font-bold leading-none text-[#111315]">{formatCompactSigned(totalImpact)}</strong>
                {!miniview && <p className="mt-2 text-[0.625em] text-[#4E535B]">{t('Incremental volume across treated rows in the measurement period.')}</p>}
            </article>

            <article className="rounded-[0.5rem] bg-[#EFF1F3] px-4 py-3 shadow-sm">
                <span className="block text-[0.75em] font-semibold text-[#111315]">{t('Wald p-value')}</span>
                <strong className="mt-2 block text-[1.625em] font-bold leading-none text-[#111315]">{formatPlain(waldPValue, 3)}</strong>
                {!miniview && <p className="mt-2 text-[0.625em] text-[#4E535B]">{t('Below 0.05 means the measured effect is unlikely to be random variation.')}</p>}
            </article>
        </div>
    )
}

export default DmlCards
